// ============================================
// RESEARCH ENGINE - TOP 10 TRADE IDEAS PAGE
// Surfaces liquid, mispriced stocks through 7-step framework
// ============================================

import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useUserData } from '../contexts/UserContext';
import {
  ResearchRun,
  ResearchResult,
  ResearchEngineConfig,
  MispricingTag,
  DEFAULT_LIQUID_UNIVERSE
} from '../types';
import { runResearchEngine, DEFAULT_RESEARCH_CONFIG } from '../services/researchEngine';
import {
  Search, Lock, Crown, AlertCircle, TrendingUp, TrendingDown,
  ChevronRight, BarChart3, Target, Zap, Filter, RefreshCw,
  ArrowUpDown, Info, Play, Pause, CheckCircle2, XCircle
} from 'lucide-react';

type ViewState = 'config' | 'running' | 'results';

export const ResearchEnginePage: React.FC = () => {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useUser();
  const { userData, loading: userLoading } = useUserData();

  const [view, setView] = useState<ViewState>('config');
  const [run, setRun] = useState<Partial<ResearchRun> | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Config state
  const [universeSource, setUniverseSource] = useState<'manual' | 'watchlist' | 'most_active'>('most_active');
  const [manualTickers, setManualTickers] = useState('');
  const [maxResults, setMaxResults] = useState(10);
  const [sortBy, setSortBy] = useState<'score' | 'mispricing' | 'probability'>('score');
  const [includeTags, setIncludeTags] = useState<MispricingTag[]>(['UNDERVALUED', 'OVERVALUED']);
  const [riskTolerance, setRiskTolerance] = useState<5 | 10>(5);

  // Filter state for results
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterDirection, setFilterDirection] = useState<string>('all');

  const handleStartResearch = useCallback(async () => {
    setView('running');
    setError(null);

    const config: Partial<ResearchEngineConfig> = {
      universeSource,
      manualTickers: universeSource === 'manual'
        ? manualTickers.split(/[,\s]+/).filter(t => t.length > 0).map(t => t.toUpperCase())
        : undefined,
      maxResults,
      sortBy,
      includeTags,
      riskTolerance,
      maxConcurrentAnalysis: 2 // Rate limit protection
    };

    try {
      const result = await runResearchEngine(
        config,
        userData?.clerkId,
        (progress) => setRun(progress)
      );
      setRun(result);
      setView('results');
    } catch (err: any) {
      console.error('Research engine failed:', err);
      setError(err.message || 'Research analysis failed');
      setView('config');
    }
  }, [universeSource, manualTickers, maxResults, sortBy, includeTags, riskTolerance, userData]);

  const handleViewFullAnalysis = (result: ResearchResult) => {
    // Navigate to scanner with the full analysis cached
    if (result.fullAnalysis) {
      sessionStorage.setItem('lastAnalysis', JSON.stringify(result.fullAnalysis));
      navigate(`/scanner?ticker=${result.ticker}`);
    }
  };

  const handleConsultMoonshot = (result: ResearchResult) => {
    // Navigate to Moonshot with context
    navigate('/moonshot', {
      state: {
        ticker: result.ticker,
        score: result.tradeScore,
        status: result.status,
        mispricingTag: result.mispricingTag,
        strategyName: result.strategyName,
        direction: result.direction
      }
    });
  };

  // Filter results
  const filteredResults = run?.results?.filter(r => {
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (filterDirection !== 'all' && r.direction !== filterDirection) return false;
    return true;
  }) || [];

  // Loading state
  if (!isLoaded || userLoading) {
    return (
      <div className="min-h-screen bg-terminal-bg flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-terminal-dim">Loading research engine...</p>
        </div>
      </div>
    );
  }

  // Not signed in
  if (!isSignedIn) {
    return (
      <div className="min-h-screen bg-terminal-bg flex flex-col items-center justify-center p-4">
        <div className="max-w-md text-center">
          <Lock className="w-16 h-16 text-terminal-dim mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Sign In Required</h1>
          <p className="text-terminal-dim mb-6">
            Please sign in to access the Research Engine.
          </p>
          <Link
            to="/login"
            className="block w-full py-3 px-6 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black rounded-lg font-bold hover:opacity-90"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  // No subscription
  if (!userData?.canScan) {
    return (
      <div className="min-h-screen bg-terminal-bg flex flex-col items-center justify-center p-4">
        <div className="max-w-md text-center">
          <Crown className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Subscription Required</h1>
          <p className="text-terminal-dim mb-6">
            The Research Engine requires an active subscription.
          </p>
          <Link
            to="/pricing"
            className="block w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-orange-500 text-black rounded-lg font-bold hover:opacity-90"
          >
            View Plans
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-terminal-bg">
      {/* Header */}
      <header className="border-b border-terminal-border bg-terminal-bg/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Search className="w-6 h-6 text-cyan-400" />
              <h1 className="text-xl font-bold text-white">Research Engine</h1>
              <span className="px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded">BETA</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-terminal-dim text-sm">
                {userData?.remainingScans} scans remaining
              </span>
              <Link
                to="/scanner"
                className="text-cyan-400 hover:text-cyan-300 text-sm"
              >
                Single Scan
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Config View */}
        {view === 'config' && (
          <div className="space-y-6">
            {/* Description */}
            <div className="bg-terminal-card border border-terminal-border rounded-lg p-6">
              <h2 className="text-lg font-bold text-white mb-2">Top 10 Trade Ideas Generator</h2>
              <p className="text-terminal-dim text-sm">
                The Research Engine scans a universe of liquid options stocks, identifies mispriced opportunities
                (undervalued or overvalued), and runs each through the 7-step framework to surface the best trade ideas.
              </p>
              <div className="mt-4 flex items-start gap-2 text-amber-400 text-sm">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Each ticker analyzed counts as 1 scan. Running on 50 tickers will use 50 scans.
                  Results include probabilities and strategies but are research-only, not financial advice.
                </span>
              </div>
            </div>

            {/* Configuration */}
            <div className="bg-terminal-card border border-terminal-border rounded-lg p-6">
              <h3 className="text-white font-bold mb-4">Configuration</h3>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Universe Source */}
                <div>
                  <label className="block text-sm font-medium text-terminal-bright mb-2">
                    Universe Source
                  </label>
                  <select
                    value={universeSource}
                    onChange={(e) => setUniverseSource(e.target.value as any)}
                    className="w-full bg-terminal-bg border border-terminal-border rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  >
                    <option value="most_active">Most Active Options (AI-Screened)</option>
                    <option value="watchlist">My Watchlist</option>
                    <option value="manual">Manual Ticker List</option>
                  </select>
                </div>

                {/* Risk Tolerance */}
                <div>
                  <label className="block text-sm font-medium text-terminal-bright mb-2">
                    Risk Tolerance
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setRiskTolerance(5)}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
                        riskTolerance === 5
                          ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500'
                          : 'bg-terminal-bg border border-terminal-border text-terminal-dim hover:text-white'
                      }`}
                    >
                      Conservative (5%)
                    </button>
                    <button
                      onClick={() => setRiskTolerance(10)}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
                        riskTolerance === 10
                          ? 'bg-amber-500/20 text-amber-400 border border-amber-500'
                          : 'bg-terminal-bg border border-terminal-border text-terminal-dim hover:text-white'
                      }`}
                    >
                      Aggressive (10%)
                    </button>
                  </div>
                </div>

                {/* Manual Tickers */}
                {universeSource === 'manual' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-terminal-bright mb-2">
                      Tickers (comma or space separated)
                    </label>
                    <textarea
                      value={manualTickers}
                      onChange={(e) => setManualTickers(e.target.value)}
                      placeholder="AAPL, MSFT, GOOGL, TSLA, NVDA..."
                      className="w-full bg-terminal-bg border border-terminal-border rounded-lg px-4 py-2 text-white h-24 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    />
                    <p className="text-terminal-dim text-xs mt-1">
                      {manualTickers.split(/[,\s]+/).filter(t => t.length > 0).length} tickers entered
                    </p>
                  </div>
                )}

                {/* Mispricing Filter */}
                <div>
                  <label className="block text-sm font-medium text-terminal-bright mb-2">
                    Include Mispricing Types
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (includeTags.includes('UNDERVALUED')) {
                          setIncludeTags(includeTags.filter(t => t !== 'UNDERVALUED'));
                        } else {
                          setIncludeTags([...includeTags, 'UNDERVALUED']);
                        }
                      }}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition flex items-center justify-center gap-1 ${
                        includeTags.includes('UNDERVALUED')
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500'
                          : 'bg-terminal-bg border border-terminal-border text-terminal-dim'
                      }`}
                    >
                      <TrendingUp className="w-4 h-4" /> Undervalued
                    </button>
                    <button
                      onClick={() => {
                        if (includeTags.includes('OVERVALUED')) {
                          setIncludeTags(includeTags.filter(t => t !== 'OVERVALUED'));
                        } else {
                          setIncludeTags([...includeTags, 'OVERVALUED']);
                        }
                      }}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition flex items-center justify-center gap-1 ${
                        includeTags.includes('OVERVALUED')
                          ? 'bg-rose-500/20 text-rose-400 border border-rose-500'
                          : 'bg-terminal-bg border border-terminal-border text-terminal-dim'
                      }`}
                    >
                      <TrendingDown className="w-4 h-4" /> Overvalued
                    </button>
                  </div>
                </div>

                {/* Sort By */}
                <div>
                  <label className="block text-sm font-medium text-terminal-bright mb-2">
                    Sort Results By
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="w-full bg-terminal-bg border border-terminal-border rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  >
                    <option value="score">Trade Score (Highest First)</option>
                    <option value="mispricing">Mispricing Score (Strongest First)</option>
                    <option value="probability">Probability of Profit</option>
                  </select>
                </div>
              </div>

              {/* Action Button */}
              <div className="mt-6">
                <button
                  onClick={handleStartResearch}
                  disabled={includeTags.length === 0}
                  className="w-full py-4 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black rounded-lg font-bold text-lg hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Play className="w-5 h-5" />
                  Start Research Analysis
                </button>
                {error && (
                  <div className="mt-4 p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg flex items-center gap-2 text-rose-400">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Default Universe Preview */}
            <div className="bg-terminal-card border border-terminal-border rounded-lg p-6">
              <h3 className="text-white font-bold mb-3">Default Liquid Universe ({DEFAULT_LIQUID_UNIVERSE.length} stocks)</h3>
              <div className="flex flex-wrap gap-2">
                {DEFAULT_LIQUID_UNIVERSE.slice(0, 20).map(ticker => (
                  <span key={ticker} className="px-2 py-1 bg-terminal-bg rounded text-terminal-dim text-xs font-mono">
                    {ticker}
                  </span>
                ))}
                {DEFAULT_LIQUID_UNIVERSE.length > 20 && (
                  <span className="px-2 py-1 text-terminal-dim text-xs">
                    +{DEFAULT_LIQUID_UNIVERSE.length - 20} more
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Running View */}
        {view === 'running' && run && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-terminal-card border border-terminal-border rounded-lg p-8 text-center">
              <div className="relative w-32 h-32 mx-auto mb-6">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    className="text-terminal-border"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${(run.progress || 0) * 3.52} 352`}
                    className="text-cyan-400 transition-all duration-300"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-3xl font-bold text-white">{run.progress || 0}%</span>
                </div>
              </div>

              <h2 className="text-xl font-bold text-white mb-2">Analyzing Universe</h2>
              <p className="text-terminal-dim mb-4">
                {run.currentTicker ? `Currently analyzing: ${run.currentTicker}` : 'Preparing...'}
              </p>

              <div className="flex justify-center gap-8 text-sm">
                <div>
                  <span className="text-terminal-dim">Universe:</span>
                  <span className="text-white ml-2">{run.universeCount || 0} tickers</span>
                </div>
                <div>
                  <span className="text-terminal-dim">Analyzed:</span>
                  <span className="text-white ml-2">{run.results?.length || 0}</span>
                </div>
                <div>
                  <span className="text-terminal-dim">Passed:</span>
                  <span className="text-emerald-400 ml-2">{run.results?.filter(r => r.status !== 'NO_TRADE').length || 0}</span>
                </div>
              </div>

              {run.errors && run.errors.length > 0 && (
                <div className="mt-6 text-left">
                  <p className="text-amber-400 text-sm mb-2">{run.errors.length} errors:</p>
                  <div className="max-h-24 overflow-y-auto text-xs text-terminal-dim">
                    {run.errors.slice(0, 5).map((e, i) => (
                      <div key={i}>{e.ticker}: {e.error}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Results View */}
        {view === 'results' && run && (
          <div className="space-y-6">
            {/* Summary Bar */}
            <div className="bg-terminal-card border border-terminal-border rounded-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">{run.universeCount}</div>
                    <div className="text-xs text-terminal-dim">Universe</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-cyan-400">{run.results?.length || 0}</div>
                    <div className="text-xs text-terminal-dim">Analyzed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-400">{run.topTen?.length || 0}</div>
                    <div className="text-xs text-terminal-dim">Top Ideas</div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="bg-terminal-bg border border-terminal-border rounded px-3 py-1.5 text-sm text-white"
                  >
                    <option value="all">All Status</option>
                    <option value="TRADE">TRADE Only</option>
                    <option value="CAUTION">CAUTION</option>
                  </select>
                  <select
                    value={filterDirection}
                    onChange={(e) => setFilterDirection(e.target.value)}
                    className="bg-terminal-bg border border-terminal-border rounded px-3 py-1.5 text-sm text-white"
                  >
                    <option value="all">All Directions</option>
                    <option value="BULLISH">Bullish</option>
                    <option value="BEARISH">Bearish</option>
                    <option value="NEUTRAL">Neutral</option>
                  </select>
                  <button
                    onClick={() => setView('config')}
                    className="px-4 py-1.5 bg-terminal-bg border border-terminal-border rounded text-cyan-400 hover:bg-terminal-border flex items-center gap-1 text-sm"
                  >
                    <RefreshCw className="w-4 h-4" /> New Run
                  </button>
                </div>
              </div>
            </div>

            {/* Top 10 Grid */}
            {run.topTen && run.topTen.length > 0 && (
              <div>
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-400" />
                  Top {run.topTen.length} Trade Ideas
                </h3>
                <div className="grid gap-4">
                  {run.topTen.map((result, index) => (
                    <ResultCard
                      key={result.ticker}
                      result={result}
                      rank={index + 1}
                      onViewAnalysis={() => handleViewFullAnalysis(result)}
                      onConsultMoonshot={() => handleConsultMoonshot(result)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* All Results */}
            {filteredResults.length > 0 && (
              <div>
                <h3 className="text-white font-bold mb-4">All Results ({filteredResults.length})</h3>
                <div className="bg-terminal-card border border-terminal-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-terminal-border">
                        <th className="px-4 py-3 text-left text-terminal-dim font-medium">Ticker</th>
                        <th className="px-4 py-3 text-left text-terminal-dim font-medium">Score</th>
                        <th className="px-4 py-3 text-left text-terminal-dim font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-terminal-dim font-medium">Mispricing</th>
                        <th className="px-4 py-3 text-left text-terminal-dim font-medium">Strategy</th>
                        <th className="px-4 py-3 text-left text-terminal-dim font-medium">P(Profit)</th>
                        <th className="px-4 py-3 text-left text-terminal-dim font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredResults.map((result) => (
                        <tr key={result.ticker} className="border-b border-terminal-border/50 hover:bg-terminal-bg/50">
                          <td className="px-4 py-3 font-mono font-bold text-white">{result.ticker}</td>
                          <td className="px-4 py-3">
                            <span className={`font-bold ${
                              result.tradeScore >= 70 ? 'text-emerald-400' :
                              result.tradeScore >= 50 ? 'text-amber-400' : 'text-rose-400'
                            }`}>
                              {result.tradeScore.toFixed(0)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={result.status} />
                          </td>
                          <td className="px-4 py-3">
                            <MispricingBadge tag={result.mispricingTag} score={result.mispricingScore} />
                          </td>
                          <td className="px-4 py-3 text-terminal-dim">{result.strategyName}</td>
                          <td className="px-4 py-3 text-terminal-dim">{result.probabilityOfProfit.toFixed(0)}%</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleViewFullAnalysis(result)}
                              className="text-cyan-400 hover:text-cyan-300 text-xs"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Empty State */}
            {(!run.topTen || run.topTen.length === 0) && (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-terminal-dim mx-auto mb-4" />
                <h3 className="text-white font-bold mb-2">No Trade Ideas Found</h3>
                <p className="text-terminal-dim">
                  No stocks passed the mispricing and liquidity filters. Try adjusting your configuration.
                </p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

// ============ SUB-COMPONENTS ============

const ResultCard: React.FC<{
  result: ResearchResult;
  rank: number;
  onViewAnalysis: () => void;
  onConsultMoonshot: () => void;
}> = ({ result, rank, onViewAnalysis, onConsultMoonshot }) => {
  return (
    <div className="bg-terminal-card border border-terminal-border rounded-lg p-4 hover:border-cyan-500/50 transition">
      <div className="flex items-start justify-between gap-4">
        {/* Left: Rank + Ticker */}
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
            rank === 1 ? 'bg-amber-500/20 text-amber-400' :
            rank === 2 ? 'bg-gray-400/20 text-gray-300' :
            rank === 3 ? 'bg-orange-600/20 text-orange-400' :
            'bg-terminal-bg text-terminal-dim'
          }`}>
            {rank}
          </div>
          <div>
            <div className="font-mono text-xl font-bold text-white">{result.ticker}</div>
            <div className="flex items-center gap-2 mt-1">
              <MispricingBadge tag={result.mispricingTag} score={result.mispricingScore} />
              <StatusBadge status={result.status} />
            </div>
          </div>
        </div>

        {/* Center: Metrics */}
        <div className="flex-1 grid grid-cols-4 gap-4 mx-4">
          <div className="text-center">
            <div className={`text-2xl font-bold ${
              result.tradeScore >= 70 ? 'text-emerald-400' :
              result.tradeScore >= 50 ? 'text-amber-400' : 'text-rose-400'
            }`}>
              {result.tradeScore.toFixed(0)}
            </div>
            <div className="text-xs text-terminal-dim">Score</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-medium text-white">{result.probabilityOfProfit.toFixed(0)}%</div>
            <div className="text-xs text-terminal-dim">P(Profit)</div>
          </div>
          <div className="text-center">
            <div className={`text-lg font-medium ${
              result.direction === 'BULLISH' ? 'text-emerald-400' :
              result.direction === 'BEARISH' ? 'text-rose-400' : 'text-cyan-400'
            }`}>
              {result.direction}
            </div>
            <div className="text-xs text-terminal-dim">Direction</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-medium text-white">{result.riskRewardRatio.toFixed(1)}:1</div>
            <div className="text-xs text-terminal-dim">R:R</div>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex flex-col gap-2">
          <button
            onClick={onViewAnalysis}
            className="px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded text-sm hover:bg-cyan-500/30 flex items-center gap-1"
          >
            <BarChart3 className="w-4 h-4" /> Details
          </button>
          <button
            onClick={onConsultMoonshot}
            className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded text-sm hover:bg-emerald-500/30 flex items-center gap-1"
          >
            <Zap className="w-4 h-4" /> Moonshot
          </button>
        </div>
      </div>

      {/* Strategy Summary */}
      <div className="mt-4 pt-4 border-t border-terminal-border/50">
        <div className="flex items-center justify-between text-sm">
          <div>
            <span className="text-terminal-dim">Strategy:</span>
            <span className="text-white ml-2">{result.strategyName}</span>
            <span className={`ml-2 ${result.creditDebit === 'CREDIT' ? 'text-emerald-400' : 'text-amber-400'}`}>
              ({result.creditDebit})
            </span>
          </div>
          <div className="flex items-center gap-4 text-terminal-dim">
            <span>Max Profit: <span className="text-emerald-400">${result.maxProfit.toFixed(0)}</span></span>
            <span>Max Loss: <span className="text-rose-400">${result.maxLoss.toFixed(0)}</span></span>
            <span>IV Rank: <span className="text-cyan-400">{result.ivRank}</span></span>
          </div>
        </div>
        {result.mispricingReasoning && result.mispricingReasoning.length > 0 && (
          <div className="mt-2 text-xs text-terminal-dim">
            <span className="text-amber-400">Thesis:</span> {result.mispricingReasoning[0]}
          </div>
        )}
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config = {
    TRADE: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2 },
    CAUTION: { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: AlertCircle },
    NO_TRADE: { bg: 'bg-rose-500/20', text: 'text-rose-400', icon: XCircle }
  }[status] || { bg: 'bg-terminal-bg', text: 'text-terminal-dim', icon: Info };

  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
};

const MispricingBadge: React.FC<{ tag: MispricingTag; score: number }> = ({ tag, score }) => {
  if (tag === 'UNDERVALUED') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/20 text-emerald-400">
        <TrendingUp className="w-3 h-3" /> Undervalued (+{score})
      </span>
    );
  }
  if (tag === 'OVERVALUED') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-rose-500/20 text-rose-400">
        <TrendingDown className="w-3 h-3" /> Overvalued ({score})
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-terminal-bg text-terminal-dim">
      Fair
    </span>
  );
};

export default ResearchEnginePage;
